import React, { useState, useCallback } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import some from 'lodash/some';

import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import companyPhoneNumbersResource from 'src/actions/companyPhoneNumber/companyPhoneNumbersResource';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import SetupSmsModal from 'src/presentation/components/pages/channels/shared/setupPhoneModal/SetupSmsModal';
import SmsEmptyPage from './SmsEmptyPage';
import SmsDescription from './SmsDescription';
import SmsEditor from './SmsEditor';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { useSms } from 'src/presentation/components/pages/channels/sms/hooks/useSms';

const PHONE_NUMBER_COLUMN = {
  attr: 'number',
  label: 'Phone number',
  customColumnStyle: () => ({ width: '200px' }),
  sorting: true,
  customRenderer: item => formatPhone(item.number),
};
const INBOX_COLUMN = { attr: 'inbox', label: 'Inbox', sorting: true };
const INTERNAL_NAME_COLUMN = { attr: 'name', label: 'Internal name', customColumnStyle: () => ({ width: '240px' }) };

const columnsConfigWithInternalName = [PHONE_NUMBER_COLUMN, INTERNAL_NAME_COLUMN, INBOX_COLUMN];

const pageDataAid = 'admin-sms-page';

const sortingConfig = { attr: 'number' };

const TableActions = ({ handleManageSms }): JSX.Element => (
  <Button data-aid="action-button-manage-sms" onClick={handleManageSms} buttonType={ButtonTypes.PRIMARY}>
    Set up SMS
  </Button>
);

const Sms: TablePageRender = ({ root }) => {
  const { items, loadingState, resetErrors, errors } = useSms();
  const [showModal, openModal] = useState(false);
  const handleCloseModal = useCallback(() => openModal(false), []);
  const handleManageSms = useCallback(() => openModal(true), []);

  const renderRowActions = ({ item }) => <Edit id={item.id} />;

  // preload needed resources
  useResource(companyPhoneNumbersResource);
  useResource(communicationQueuesResource);
  useResource(routingGroupsResource);

  return (
    <>
      {isLoadingStatus(loadingState) && items.length === 0 ? (
        <Spinner />
      ) : items.length === 0 ? (
        <SmsEmptyPage handleManageSms={handleManageSms} />
      ) : (
        <TablePage
          actions={<TableActions handleManageSms={handleManageSms} />}
          editor={SmsEditor}
          columnConfig={columnsConfigWithInternalName}
          data-aid={pageDataAid}
          description={<SmsDescription pageDataAid={pageDataAid} />}
          errors={errors}
          filterFunc={filterFunc}
          filterPlaceholder="Search by digits or Inbox"
          initialSorting={sortingConfig}
          isLoading={isLoadingStatus(loadingState)}
          items={items}
          name="SMS"
          renderRowActions={renderRowActions}
          resetErrors={resetErrors}
          root={root}
        />
      )}
      {showModal && <SetupSmsModal handleCloseModal={handleCloseModal} />}
    </>
  );
};

const filterFunc = (items, searchString) => {
  const searchPaths = ['number', 'inbox'];
  if (searchString) {
    const search = searchString.toLowerCase().trim();
    return filter(items, item =>
      some(searchPaths, columnKey => get(item, columnKey, '').toLowerCase().includes(search))
    );
  }
  return items;
};

export default Sms;
